import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileText, ClipboardList, Package, Truck, Wrench, Users, ArrowRight, Bookmark, LayoutGrid } from 'lucide-react';

const TILES = [
  { type: 'WO2', path: '/orders/work',        icon: Wrench,        label: 'Work Orders',        blurb: 'NEW · Unified Production/Repair/Warranty/Refurb/Rework/R&D (Peak Prairie form)' },
  { type: 'SO',  path: '/orders/sales',       icon: FileText,      label: 'Sales Orders',       blurb: 'Customer-facing commitments' },
  { type: 'PO',  path: '/orders/production',  icon: ClipboardList, label: 'Production Orders',  blurb: 'Legacy — superseded by Work Orders (still editable)' },
  { type: 'PS',  path: '/orders/packing',     icon: Package,       label: 'Packing Slips',      blurb: 'What left the dock, serial by serial' },
  { type: 'BOL', path: '/orders/bol',         icon: Truck,         label: 'Bills of Lading',    blurb: 'Carrier handoff + chain of custody' },
  { type: 'RO',  path: '/orders/repairs',     icon: Wrench,        label: 'Lyphline Repair Orders',      blurb: 'Legacy — superseded by Work Orders (still editable)' },
];

const OrdersHomePage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [counters, setCounters] = useState([]);
  const [customerCount, setCustomerCount] = useState(0);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [c, cust, tpl] = await Promise.all([
          api().get('/document-numbering'),
          api().get('/customers'),
          api().get('/doc-templates'),
        ]);
        setCounters(c.data || []);
        setCustomerCount((cust.data || []).length);
        setTemplates(tpl.data || []);
      } catch { /* non-blocking */ }
    })();
  }, []);

  const nextFor = (type) => (counters.find(c => c.type === type) || {}).example;

  const TPL_META = {
    sales_order:    { label: 'Sales Order',    newPath: '/orders/sales/new',   testIdType: 'so' },
    bill_of_lading: { label: 'Bill of Lading', newPath: '/orders/bol/new',     testIdType: 'bol' },
  };

  return (
    <div className="space-y-4" data-testid="orders-home-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Orders &amp; Shipping</h1>
          <p className="text-sm text-slate-500 mt-1">The customer-facing document stack: Sales → Production → Packing → BoL. Repair Orders run on their own track. Every doc exports to CRM.</p>
        </div>
        <Button asChild variant="outline" className="rounded-sm" data-testid="orders-home-overview-btn">
          <Link to="/orders/overview"><LayoutGrid className="w-4 h-4 mr-1" /> Overview Search</Link>
        </Button>
      </div>

      <Card className="industrial-card bg-gradient-to-r from-slate-50 to-white border-slate-200">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-slate-600" />
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">Customer master</p>
              <p className="text-lg font-semibold text-slate-900">{customerCount} customer{customerCount === 1 ? '' : 's'}</p>
            </div>
          </div>
          <Button asChild variant="outline" className="rounded-sm" data-testid="orders-home-customers-btn">
            <Link to="/customers">Manage Customers <ArrowRight className="w-4 h-4 ml-1" /></Link>
          </Button>
        </CardContent>
      </Card>

      {templates.length > 0 && (
        <Card className="industrial-card border-amber-200 bg-amber-50/40" data-testid="orders-home-templates">
          <CardHeader className="border-b border-amber-100 pb-3 flex-row items-center gap-2 space-y-0">
            <Bookmark className="w-4 h-4 text-amber-700" />
            <CardTitle className="text-base">New from Template</CardTitle>
            <span className="text-[11px] text-slate-500 ml-auto">Skip the empty form — pre-fill customer, terms &amp; ship-from.</span>
          </CardHeader>
          <CardContent className="p-3 flex flex-wrap gap-2">
            {templates.map(t => {
              const meta = TPL_META[t.doc_type];
              if (!meta) return null;
              return (
                <Button
                  key={t.id}
                  variant="outline"
                  size="sm"
                  className="rounded-sm border-amber-300 bg-white hover:bg-amber-100"
                  data-testid={`orders-home-tpl-${t.id}`}
                  onClick={() => navigate(`${meta.newPath}?tpl=${t.id}`)}
                >
                  <span className="mono text-[10px] uppercase tracking-wider text-amber-700 mr-2">{meta.testIdType}</span>
                  {t.name}
                </Button>
              );
            })}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {TILES.map(t => {
          const Icon = t.icon;
          return (
            <Card key={t.type} className="industrial-card hover:shadow-md transition-shadow">
              <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base flex items-center gap-2"><Icon className="w-5 h-5 text-slate-700" /> {t.label}</CardTitle>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 mono">{t.type}</span>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <p className="text-xs text-slate-500">{t.blurb}</p>
                {nextFor(t.type) && (
                  <div className="text-xs">
                    <span className="text-slate-500">Next number:</span>{' '}
                    <span className="mono font-semibold text-slate-900">{nextFor(t.type)}</span>
                  </div>
                )}
                <Button asChild size="sm" variant="outline" className="w-full rounded-sm" data-testid={`orders-home-${t.type.toLowerCase()}-link`}>
                  <Link to={t.path}>Open {t.label} <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default OrdersHomePage;
